import { useEffect } from 'react';
import {
	rezaUsageDownloadQuery,
	rezaUsageSendByDataboxQuery,
	useAxiosInstance,
	usePoMutation,
	useQueryDownload,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import { getAllDataboxTypes, useFormBuilder } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type {
	FormCheckboxProps,
	DataBoxFormData,
	FormInputProps,
} from '@gov-nx/ui/types';
import {
	type DataBoxFields,
	getCheckboxDefinition,
	toStringDate,
	useDataboxDefinition,
	useDateDefinition,
} from '@gov-nx/utils/common';
import { useDataBoxStoreLists } from '@gov-nx/store';
import { useRezaLocale } from './useRezaLocale';

interface Fields extends DataBoxFields {
	sendByDatabox: FormCheckboxProps;
	datumOd: FormInputProps;
	datumDo: FormInputProps;
}

interface UsageFormData extends DataBoxFormData {
	sendByDatabox: boolean;
	datumOd: Nullable<Date>;
	datumDo: Nullable<Date>;
}
const USAGE_FORM_ID = 'UsageForm';

export const useUsageForm = () => {
	const { t } = useRezaLocale();
	const { axiosInstance } = useAxiosInstance();
	const { toastMessageError, toastMessageSuccess } = useMessageEvents();
	const { getConnectedDataBoxesByTypes } = useDataBoxStoreLists();
	const hasDataBoxes =
		getConnectedDataBoxesByTypes(getAllDataboxTypes()).length > 0;
	const downloadUsage = useQueryDownload(rezaUsageDownloadQuery, {
		onDownloadSuccess: () => formDefinition.formReset(),
	});
	const sendByDataboxMutation = usePoMutation({
		mutationFn: rezaUsageSendByDataboxQuery,
		onSuccess: () => {
			toastMessageSuccess(t('usage.form.sendByDatabox.uspesne-odeslano'));
			formDefinition.formReset();
		},
		onError: () => toastMessageError(),
	});

	const isLoading =
		downloadUsage.isDownloading || sendByDataboxMutation.isLoading;

	const datumOd = useDateDefinition({
		name: 'datumOd',
		label: t('usage.form.platnost-od.label'),
		isDisabled: isLoading,
	});

	const datumDo = useDateDefinition({
		name: 'datumDo',
		label: t('usage.form.platnost-do.label'),
		noOlderThan: {
			fieldName: datumOd.field.field.name,
			errorMessage: t('usage.form.validace.platnost-do.starsi-nez-datum-od'),
		},
		isDisabled: isLoading,
	});

	const sendByDatabox = getCheckboxDefinition({
		label: t('usage.form.sendByDatabox.label'),
		name: 'sendByDatabox',
		isDisabled: isLoading,
	});

	const databox = useDataboxDefinition({
		label: t('usage.form.databox.label'),
		isDisabled: isLoading,
		types: getAllDataboxTypes(),
	});

	const formDefinition = useFormBuilder<UsageFormData, Fields>([
		datumOd,
		datumDo,
		sendByDatabox,
		...databox,
	]);

	const isSentByDatabox = formDefinition.formMethods.watch('sendByDatabox');

	useEffect(() => {
		if (!hasDataBoxes) {
			formDefinition.formMethods.setValue('sendByDatabox', false);
			formDefinition.formMethods.setValue('datovaSchrankaId', '');
		}
	}, [formDefinition.formMethods, hasDataBoxes]);

	const onSubmit = formDefinition.formMethods.handleSubmit((formData) => {
		const { datumOd, datumDo } = formData;

		const dateParams = {
			datumOd: datumOd ? toStringDate(datumOd) : undefined,
			datumDo: datumDo ? toStringDate(datumDo) : undefined,
		};

		if (hasDataBoxes && isSentByDatabox) {
			sendByDataboxMutation.mutate({
				axiosInstance,
				body: {
					...dateParams,
					odesilatelDSId: formData.datovaSchrankaId,
					naseCisloJednaci: formData.naseCisloJednaci,
					naseSpisovaZnacka: formData.naseSpisovaZnacka,
				},
			});
		} else {
			downloadUsage.download({
				axiosInstance,
				body: dateParams,
			});
		}
	});

	return {
		formDefinition,
		isSentByDatabox,
		hasDataBoxes,
		formId: USAGE_FORM_ID,
		onSubmit,
		isLoading,
		strings: {
			title: t('usage.title'),
			submitButton: t('usage.ziskat'),
			dateRangeLabel: t('usage.form.date-range.label'),
		},
	};
};
